import { log } from "../utils/logger.js";
import { ADDRESS_FETCHED_EVENT } from "../config/defaults.js";
import type { default as _SpeechSynthesisManager } from "../speech/SpeechSynthesisManager.js";
import { SPEECH_PRIORITY } from "../speech/SpeechSynthesisManager.js";
import type { SpeechTextBuilder as _SpeechTextBuilder } from "../speech/SpeechTextBuilder.js";
import PositionManager from "../core/PositionManager.js";

export class AddressSpeechObserver {
  speechManager: any;
  textBuilder: any;
  textInput: any;
  textInputResolver: (() => HTMLInputElement | HTMLTextAreaElement | null) | null;
  _firstAddressAnnounced: boolean;

  constructor(speechManager: any, textBuilder: any, textInput: any) {
    if (speechManager == null) {
      throw new TypeError(
        "SpeechManager parameter cannot be null or undefined",
      );
    }

    if (textBuilder == null) {
      throw new TypeError("TextBuilder parameter cannot be null or undefined");
    }

    this.speechManager = speechManager;
    this.textBuilder = textBuilder;
    this.textInput = typeof textInput === 'function' ? null : (textInput || null);
    this.textInputResolver = typeof textInput === 'function' ? textInput : null;
    this._firstAddressAnnounced = false;

    // NOTE: We do not freeze this instance because _firstAddressAnnounced needs
    // to be mutable to track state across update() calls
    log('(AddressSpeechObserver) created — firstAddressAnnounced reset to false');
  }

  private _getTextInput() {
    if (this.textInputResolver) {
      this.textInput = this.textInputResolver();
    }

    return this.textInput || null;
  }

  update(
    currentAddress: any,
    enderecoPadronizadoOrEvent: any,
    posEvent: string,
    loadingOrChangeDetails: any,
    _error: unknown,
  ): void {
    // Logging is now handled by HtmlSpeechSynthesisDisplayer facade for backward compatibility
    // if (typeof console !== 'undefined' && typeof console.log === 'function') {
    // log("+++ (301) AddressSpeechObserver.update called +++");
    // log("+++ (302) currentAddress: ", currentAddress);
    // log("+++ (303) enderecoPadronizadoOrEvent: ", enderecoPadronizadoOrEvent);
    // log("+++ (304) posEvent: ", posEvent);
    // }

    if (!currentAddress) {
      return;
    }

    let textToBeSpoken = "";
    let priority: any = SPEECH_PRIORITY.PERIODIC;

    if (posEvent === ADDRESS_FETCHED_EVENT && !this._firstAddressAnnounced) {
      if (typeof console !== "undefined" && typeof console.log === "function") {
        log("+++ (305) (AddressSpeechObserver) First address - attempting announcement");
      }
      textToBeSpoken = this.textBuilder.buildTextToSpeech(
        enderecoPadronizadoOrEvent,
      );
      priority = SPEECH_PRIORITY.FIRST_ADDRESS;
      // Only consume the first-address slot when there is actual text to speak.
      // If buildTextToSpeech returns empty (incomplete address data, voices not
      // loaded yet, etc.) the next ADDRESS_FETCHED_EVENT gets another attempt.
      if (textToBeSpoken) {
        this._firstAddressAnnounced = true;
        this.speechManager.speak(textToBeSpoken, priority);
        const textInput = this._getTextInput();
        if (textInput) {
          textInput.value = textToBeSpoken;
        }
      }
    } else if (
      ["MunicipioChanged", "BairroChanged", "LogradouroChanged"].includes(
        enderecoPadronizadoOrEvent,
      )
    ) {
      if (typeof console !== "undefined" && typeof console.log === "function") {
        log("+++ (310) (AddressSpeechObserver) Changed");
      }

      const fullAddress =
        loadingOrChangeDetails?.currentAddress || currentAddress;

      if (enderecoPadronizadoOrEvent === "MunicipioChanged") {
        textToBeSpoken = this.textBuilder.buildTextToSpeechMunicipio(
          fullAddress,
          loadingOrChangeDetails,
        );
        priority = SPEECH_PRIORITY.MUNICIPIO;
      } else if (enderecoPadronizadoOrEvent === "BairroChanged") {
        textToBeSpoken = this.textBuilder.buildTextToSpeechBairro(fullAddress);
        priority = SPEECH_PRIORITY.BAIRRO;
      } else if (enderecoPadronizadoOrEvent === "LogradouroChanged") {
        textToBeSpoken =
          this.textBuilder.buildTextToSpeechLogradouro(fullAddress);
        priority = SPEECH_PRIORITY.LOGRADOURO;
      }

      if (textToBeSpoken) {
        this.speechManager.speak(textToBeSpoken, priority);
        const textInput = this._getTextInput();
        if (textInput) {
          textInput.value = textToBeSpoken;
        }
      }
    } else if (posEvent === PositionManager.strCurrPosUpdate) {
      textToBeSpoken = this.textBuilder.buildTextToSpeech(
        enderecoPadronizadoOrEvent,
      );
      priority = SPEECH_PRIORITY.PERIODIC;
      const textInput = this._getTextInput();
      if (textInput) {
        textInput.value = textToBeSpoken;
      }

      this.speechManager.speak(textToBeSpoken, priority);
    }
  }

  resetFirstAddressFlag(): void {
    this._firstAddressAnnounced = false;
  }

  hasAnnouncedFirstAddress(): boolean {
    return this._firstAddressAnnounced;
  }

  toString(): string {
    return this.constructor.name;
  }
}

export default AddressSpeechObserver;
